import { useState, useCallback } from "react";

import { readItems } from "@directus/sdk";
import { message } from "antd";
import { getDirectusClient } from "../../../lib/directus";



export function useLoans(vendor) {
  const [loans, setLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);

  const getLoans = useCallback(async () => {
    if (!vendor?.id) {
      setLoans([]);
      return;
    }
    setLoadingLoans(true);
    let directus = await getDirectusClient();
    try {
      const res = await directus.request(
        readItems("prestamos", {
          fields: [
            "id",
            "tipo",
            "estado",
            "vendedor",
            "inventario_nsp.*",
            "inventario_nsp.inventario_ns_prestamo_id.*",
            "inventario_nsp.inventario_ns_prestamo_id.inventario_ns.*",
            "inventario_nsp.inventario_ns_prestamo_id.inventario_ns.producto.*",
            "inventario_nsp.inventario_ns_prestamo_id.inventario_ns.producto.grupo.impuesto",
            "inventario_nsp.inventario_ns_prestamo_id.inventario_ns.producto.preciosinimp",
          ],
          filter: {
            _and: [
              { vendedor: { _eq: vendor.id } },
              { tipo: { _eq: "prestamo" } },
              { estado: { _eq: "activo" } },
            ],
          },
        })
      );
      if (Array.isArray(res) && res.length > 0) {
        const allProducts = [];
        for (const loan of res) {
          const uniqueProducts = new Set();
          const serials = await directus.request(
            readItems("seriales", {
              fields: [
                "producto.id",
                "producto.nombre",
                "producto.preciosinimp",
                "producto.grupo.impuesto",
              ],
              filter: {
                _and: [
                  { prestamo: { prestamos_id: { id: { _eq: loan.id } } } },
                  { estado: { _eq: 3 } },
                  { user: { _eq: vendor.id } },
                ],
              },
              limit: -1,
            })
          );
          serials.forEach((s) => {
            if (s.producto?.id) uniqueProducts.add(s.producto.id);
          });
          for (const productId of uniqueProducts) {
            const countResponse = await directus.request(
              readItems("seriales", {
                aggregate: { countDistinct: ["id"] },
                filter: {
                  _and: [
                    { prestamo: { prestamos_id: { id: { _eq: loan.id } } } },
                    { estado: { _eq: 3 } },
                    { user: { _eq: vendor.id } },
                    { producto: { id: { _eq: productId } } },
                  ],
                },
              })
            );
            const productInfo = serials.find((s) => s.producto?.id === productId)?.producto;
            if (productInfo) {
              const alreadyExists = allProducts.some(
                (p) => p.productId === productId && p.loanId === loan.id
              );
              if (!alreadyExists) {
                allProducts.push({
                  product: productInfo.nombre,
                  quantity: countResponse[0]?.countDistinct?.id || 0,
                  price: parseFloat(productInfo.preciosinimp || 0),
                  tax: parseFloat(productInfo.grupo?.impuesto || 0),
                  productId,
                  loanId: loan.id,
                  isNotSerialized: false,
                });
              }
            }
          }
          loan.inventario_nsp?.forEach((ns) => {
            if (ns.inventario_ns_prestamo_id?.estado === "prestado") {
              allProducts.push({
                product: ns.inventario_ns_prestamo_id?.inventario_ns?.nombre || "Producto desconocido",
                quantity: parseInt(ns.inventario_ns_prestamo_id?.cantidad || 0),
                price: parseFloat(
                  ns.inventario_ns_prestamo_id?.inventario_ns?.producto?.preciosinimp || 0
                ),
                tax: parseFloat(
                  ns.inventario_ns_prestamo_id?.inventario_ns?.producto?.grupo?.impuesto || 0
                ),
                productId: ns.inventario_ns_prestamo_id?.inventario_ns?.producto?.id,
                loanId: loan.id,
                isNotSerialized: true,
              });
            }
          });
        }
        const finalProducts = allProducts.map((product) => {
          const subtotal = product.quantity * product.price;
          const tax = (subtotal * product.tax) / 100;
          return {
            ...product,
            partialTotal: (subtotal + tax).toFixed(2),
            key: product.product,
          };
        });
        if (finalProducts.length === 0) {
          message.info(
            `${vendor.first_name} ${vendor.last_name} no tiene préstamos pendientes`
          );
        }
        setLoans(finalProducts);
      } else {
        setLoans([]);
        if (vendor.id) {
          message.info(
            `${vendor.first_name} ${vendor.last_name} no tiene préstamos pendientes`
          );
        }
      }
    } catch (err) {
      console.error("Error in getLoans:", err);
      message.error("Error al consultar los préstamos");
      setLoans([]);
    } finally {
      setLoadingLoans(false);
    }
  }, [vendor]);

  return { loans, loadingLoans, getLoans };
} 